/**
 * Cross-platform Pressable with platform-native interactions.
 *
 * iOS   — GHPressable pass-through; consumers drive scale + opacity via
 *         pressed state (Apple HIG: press == tactile scale).
 * Android — Material ripple (no scale — ripple IS the press feedback).
 * Web   — Hover lift + press scale + keyboard :focus-visible ring.
 */
import React from 'react'
import {
  type GestureResponderEvent,
  Platform,
  Pressable as RNPressable,
  type PressableProps as RNPressableProps,
  type StyleProp,
  type ViewStyle,
} from 'react-native'
import { Pressable as GHPressable } from 'react-native-gesture-handler'
import { useReducedMotion } from 'react-native-reanimated'
import { COLORS } from '@/lib/brand'
import { DURATION } from '@/lib/theme/tokens'
import { productTelemetryService } from '@/lib/services/product-telemetry.service'

type WebPressableProps = RNPressableProps & {
  dataSet?: Record<string, boolean | number | string | undefined>
}

type TelemetryIdentity = {
  elementId: string | null
  elementIdQuality: 'stable' | 'stable_auto'
  elementLabel: string | null
}

function pressableIdentityForTelemetry({
  accessibilityLabel,
  dataSet,
  testID,
}: {
  accessibilityLabel?: string
  dataSet?: Record<string, boolean | number | string | undefined>
  testID?: string
}): TelemetryIdentity {
  const explicit = dataSet?.analyticsId
  const explicitLabel = dataSet?.analyticsLabel
  const elementLabel =
    typeof explicitLabel === 'string' && explicitLabel.trim()
      ? explicitLabel.trim()
      : (accessibilityLabel?.trim() ?? null)
  if (typeof explicit === 'string' && explicit.trim()) {
    return { elementId: explicit.trim(), elementIdQuality: 'stable', elementLabel }
  }
  if (testID?.trim()) {
    return { elementId: testID.trim(), elementIdQuality: 'stable', elementLabel }
  }
  if (accessibilityLabel?.trim()) {
    return { elementId: null, elementIdQuality: 'stable_auto', elementLabel }
  }
  return { elementId: null, elementIdQuality: 'stable_auto', elementLabel: null }
}

function withTelemetryPress(
  onPress: RNPressableProps['onPress'],
  payload: {
    accessibilityLabel?: string
    accessibilityRole?: RNPressableProps['accessibilityRole']
    dataSet?: Record<string, boolean | number | string | undefined>
    testID?: string
  },
): RNPressableProps['onPress'] {
  return (event: GestureResponderEvent) => {
    const identity = pressableIdentityForTelemetry(payload)
    productTelemetryService.recordPress({
      elementId: identity.elementId,
      elementType:
        typeof payload.accessibilityRole === 'string' ? payload.accessibilityRole : 'pressable',
      metadata: {
        elementIdQuality: identity.elementIdQuality,
        elementLabel: identity.elementLabel,
      },
    })
    onPress?.(event)
  }
}

// Inject a global stylesheet once so `:focus-visible` (keyboard focus only)
// paints a coral outline. Inline styles can't express pseudo-classes, and
// RN Web's `focused` state fires on mouse clicks too — which we don't want.
if (Platform.OS === 'web' && typeof document !== 'undefined') {
  const id = '__tago_pressable_styles__'
  if (!document.getElementById(id)) {
    const el = document.createElement('style')
    el.id = id
    el.textContent = `
      [data-tagoui="pressable"] {
        -webkit-tap-highlight-color: transparent;
        outline: none;
      }
      [data-tagoui="pressable"]:focus-visible {
        outline: 2px solid ${COLORS.coral.DEFAULT};
        outline-offset: 2px;
        border-radius: inherit;
      }
    `
    document.head.appendChild(el)
  }
}

const WebPressable = React.forwardRef<React.ComponentRef<typeof RNPressable>, WebPressableProps>(
  (
    {
      style,
      disabled,
      dataSet,
      accessibilityLabel,
      accessibilityRole,
      accessibilityState,
      onPress,
      testID,
      ...rest
    },
    ref,
  ) => {
    const reducedMotion = useReducedMotion()
    const ariaProps: Record<string, unknown> = {}

    if (
      typeof accessibilityState?.checked === 'boolean' &&
      (accessibilityRole === 'checkbox' ||
        accessibilityRole === 'radio' ||
        accessibilityRole === 'switch')
    ) {
      ariaProps['aria-checked'] = accessibilityState.checked ? 'true' : 'false'
    }

    if (typeof accessibilityState?.selected === 'boolean') {
      ariaProps['aria-selected'] = accessibilityState.selected ? 'true' : 'false'
    }

    if (disabled || accessibilityState?.disabled) {
      ariaProps['aria-disabled'] = 'true'
    }

    return (
      <RNPressable
        ref={ref}
        disabled={disabled}
        accessibilityLabel={accessibilityLabel}
        accessibilityRole={accessibilityRole}
        accessibilityState={accessibilityState}
        onPress={withTelemetryPress(onPress, {
          accessibilityLabel,
          accessibilityRole,
          dataSet,
          testID,
        })}
        testID={testID}
        {...rest}
        {...ariaProps}
        {...({ dataSet: { ...dataSet, tagoui: 'pressable' } } as Record<string, unknown>)}
        style={(state) => {
          const baseStyle: StyleProp<ViewStyle> = typeof style === 'function' ? style(state) : style
          const webState = state as unknown as { hovered?: boolean; pressed?: boolean }

          const webStyles: Record<string, unknown> = {
            cursor: disabled ? 'default' : 'pointer',
            userSelect: 'none',
            touchAction: 'manipulation',
            willChange: 'transform, opacity',
            // A CSS transition is not a Reanimated animation: nothing gives it
            // the reduced-motion default for free, so it is gated by hand.
            transition: reducedMotion
              ? undefined
              : `transform ${DURATION.fast}ms cubic-bezier(0.16, 1, 0.3, 1), opacity ${DURATION.fast}ms ease, box-shadow ${DURATION.fast}ms ease`,
          }

          if (webState.hovered && !disabled) {
            webStyles.transform = 'translateY(-1px)'
          }

          if (webState.pressed && !disabled) {
            webStyles.transform = 'scale(0.98)'
            webStyles.opacity = 0.85
          }

          return [baseStyle, webStyles as ViewStyle]
        }}
      />
    )
  },
)
WebPressable.displayName = 'WebPressable'

// Android: inject a neutral, low-contrast Material ripple if the caller
// didn't set one. A saturated default ripple makes rounded chips look like a
// brief square color mask when their content is not clipped at the same level.
// Brand-colored controls already drive their own pressed state.
// Props are cast to `any` because RN's PressableProps (onHoverIn/onHoverOut)
// types don't match RNGH's — those hover handlers only matter on web anyway.
const AndroidPressable = React.forwardRef<React.ComponentRef<typeof RNPressable>, RNPressableProps>(
  ({ android_ripple, accessibilityLabel, accessibilityRole, onPress, testID, ...rest }, ref) => {
    const defaultRipple = { color: COLORS.background.cardBorder, borderless: false }
    return (
      <GHPressable
        ref={ref as never}
        android_ripple={android_ripple ?? defaultRipple}
        accessibilityLabel={accessibilityLabel}
        accessibilityRole={accessibilityRole}
        onPress={withTelemetryPress(onPress, {
          accessibilityLabel,
          accessibilityRole,
          testID,
        })}
        testID={testID}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        {...(rest as any)}
      />
    )
  },
)
AndroidPressable.displayName = 'AndroidPressable'

const NativePressable = React.forwardRef<React.ComponentRef<typeof RNPressable>, RNPressableProps>(
  ({ accessibilityLabel, accessibilityRole, onPress, testID, ...rest }, ref) => (
    <GHPressable
      ref={ref as never}
      accessibilityLabel={accessibilityLabel}
      accessibilityRole={accessibilityRole}
      onPress={withTelemetryPress(onPress, {
        accessibilityLabel,
        accessibilityRole,
        testID,
      })}
      testID={testID}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      {...(rest as any)}
    />
  ),
)
NativePressable.displayName = 'NativePressable'

export const Pressable =
  Platform.OS === 'web'
    ? (WebPressable as unknown as typeof RNPressable)
    : Platform.OS === 'android'
      ? (AndroidPressable as unknown as typeof RNPressable)
      : (NativePressable as unknown as typeof RNPressable)
